// -----------------------------------------------------------------------------
// src/components/NavBar/CreatorNavbar.js
// UPDATED: Added tutorial system integration matching main Navbar
// -----------------------------------------------------------------------------
import React, { useMemo, useEffect, useState } from "react";
import { useNavigate, Link as RouterLink, useLocation } from "react-router-dom";
import { useQuery, useMutation } from "react-query";
import { 
  Drawer, Box, Typography, List, ListItem, Divider, IconButton, 
  ListItemIcon, ListItemText, Tooltip, ListItemButton, Badge, Avatar, 
  Menu, MenuItem, useMediaQuery, Button, Collapse, CircularProgress, 
  Alert, Chip, Paper
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LiveHelpOutlinedIcon from "@mui/icons-material/LiveHelpOutlined";
import AppsIcon from "@mui/icons-material/Apps";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import StarIcon from "@mui/icons-material/Star";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import HandshakeOutlined from "@mui/icons-material/HandshakeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowLeftSharpIcon from "@mui/icons-material/KeyboardArrowLeftSharp";
import KeyboardArrowRightSharpIcon from "@mui/icons-material/KeyboardArrowRightSharp";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import VerifiedIcon from "@mui/icons-material/Verified";
import RefreshIcon from "@mui/icons-material/Refresh";
import { EmojiEvents as TrophyIcon } from "@mui/icons-material";
import SchoolIcon from "@mui/icons-material/School";

import { useCreatorAuth } from "../Hooks/creator-use-auth";
import ListItemLink from "./ListItemLink";
import routes from "../Config/routes";
import { ConfigValue } from "../Config/index.js";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import API from "../API";
import { drawerWidth } from "../Utils/constants";
import logo from "./globalAssets/logo_placeholder.png";
import ContractPopup from "./Navbar/systemComponents/ContractPopup";
import ChatModalCreator from "./ChatComponents/creatorchatmodal";
import UnifiedNotificationCenter from "./tools/UnifiedNotificationCenter";

// ═══════════════════════════════════════════════════════════
// TUTORIAL SYSTEM IMPORTS
// ═══════════════════════════════════════════════════════════
import { useTutorial } from "../BlitzHowTo/tutorialContext";
import { getTutorialForRoute } from "../BlitzHowTo/tutorialRegistry";

/* —————————————————— Enhanced Cookie Helper Functions —————————————————— */
const SUPERVISOR_CACHE_KEY = 'blitz_supervisor_cache';
const SUPERVISOR_ID_KEY = 'blitz_supervisor_id';
const STATUS_CACHE_KEY = 'blitz_creator_status_cache';
const CACHE_EXPIRY_HOURS = 24;

const setSupervisorCache = (supUserId, supervisorData) => {
  if (!supUserId || !supervisorData) return;
  
  const cacheData = {
    userId: supUserId,
    data: supervisorData,
    timestamp: new Date().getTime(),
    expiresAt: new Date().getTime() + (CACHE_EXPIRY_HOURS * 60 * 60 * 1000)
  };
  
  try {
    localStorage.setItem(SUPERVISOR_CACHE_KEY, JSON.stringify(cacheData));
    localStorage.setItem(SUPERVISOR_ID_KEY, supUserId);
    console.log('✅ [SupervisorCache] Cached supervisor data:', { supUserId });
  } catch (error) {
    console.warn('Failed to cache supervisor data:', error);
  }
};

const getSupervisorCache = () => {
  try {
    const cached = localStorage.getItem(SUPERVISOR_CACHE_KEY);
    if (!cached) return null;
    
    const cacheData = JSON.parse(cached);
    
    const now = new Date().getTime();
    if (now > cacheData.expiresAt) {
      localStorage.removeItem(SUPERVISOR_CACHE_KEY);
      localStorage.removeItem(SUPERVISOR_ID_KEY);
      return null;
    }
    
    console.log('✅ [SupervisorCache] Retrieved cached data:', { userId: cacheData.userId });
    return cacheData;
  } catch (error) {
    console.warn('Failed to read supervisor cache:', error);
    return null;
  }
};

const getLastSupervisorId = () => {
  try {
    const id = localStorage.getItem(SUPERVISOR_ID_KEY);
    console.log('🔍 [SupervisorCache] Last supervisor ID:', id);
    return id;
  } catch (error) {
    console.warn('Failed to read supervisor ID:', error);
    return null;
  }
};

const setStatusCache = (statusData) => {
  if (!statusData) return;
  
  const cacheData = {
    data: statusData,
    timestamp: new Date().getTime(),
    expiresAt: new Date().getTime() + (CACHE_EXPIRY_HOURS * 60 * 60 * 1000)
  };
  
  try {
    localStorage.setItem(STATUS_CACHE_KEY, JSON.stringify(cacheData));
    console.log('✅ [StatusCache] Cached status data');
  } catch (error) {
    console.warn('Failed to cache status data:', error);
  }
};

const getStatusCache = () => {
  try {
    const cached = localStorage.getItem(STATUS_CACHE_KEY);
    if (!cached) return null;
    
    const cacheData = JSON.parse(cached);
    
    const now = new Date().getTime();
    if (now > cacheData.expiresAt) {
      localStorage.removeItem(STATUS_CACHE_KEY);
      return null;
    }
    
    console.log('✅ [StatusCache] Retrieved cached status data');
    return cacheData.data;
  } catch (error) {
    console.warn('Failed to read status cache:', error);
    return null;
  }
};

const clearAllCaches = () => {
  try {
    localStorage.removeItem(SUPERVISOR_CACHE_KEY);
    localStorage.removeItem(SUPERVISOR_ID_KEY);
    localStorage.removeItem(STATUS_CACHE_KEY);
    console.log('🧹 [Cache] Cleared all caches');
  } catch (error) {
    console.warn('Failed to clear caches:', error);
  }
};

/* —————————————————— Drawer mixins - Enhanced —————————————————— */ 
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: 80,
});

const IconDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "extended",
})(({ theme, extended }) => ({
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(extended
    ? {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      }
    : {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      }),
}));

/* —————————————————— Enhanced Header Button —————————————————— */
const WhiteIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.04)",
  },
}));

/* —————————————————— Enhanced Bottom Section Card —————————————————— */
const BottomSectionCard = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(1.5),
  borderRadius: 12,
  boxShadow: `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(1),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  }
}));

/* —————————————————— Partner Status Chip —————————————————— */
const PartnerChip = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontWeight: 600,
  fontSize: '0.7rem',
  height: 20,
  '& .MuiChip-icon': {
    color: 'white',
  }
}));

/* —————————————————— Safe-area helpers —————————————————— */
const buildSafeAreaInset = (anchor, isDesktop) => {
  if (isDesktop) {
    return {};
  }
  return {
    pt: "env(safe-area-inset-top)",
    pb: "env(safe-area-inset-bottom)",
    pl: anchor === "left" ? "env(safe-area-inset-left)" : 0,
    pr: anchor === "right" ? "env(safe-area-inset-right)" : 0,
  };
};

export default function CreatorNavbar({
  isDesktop = true,
  isOpen = false,
  onClose,
  onToggleExtend,
  onUnreadCountChange,
  onGetMobileActions,
}) {
  const { creatorToken, logout, isInitialized } = useCreatorAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isExtended = !isDesktop || isOpen;

  /* —— Responsive breakpoints for bottom section —— */
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isVeryTall = useMediaQuery('(min-height: 800px)');
  const [bottomSectionExpanded, setBottomSectionExpanded] = useState(isLargeScreen && isVeryTall);

  /* —— Anchor based on screen size —— */
  const anchor = isDesktop ? "left" : "right";

  /* —— Notification Anchor for UnifiedNotificationCenter —— */
  const [notifAnchor, setNotifAnchor] = useState(null);

  // ═══════════════════════════════════════════════════════════
  // TUTORIAL SYSTEM HOOK
  // ═══════════════════════════════════════════════════════════
  const { startTutorial, resetTutorial } = useTutorial();

  // Check if current route has a tutorial
  const currentTutorial = useMemo(() => {
    return getTutorialForRoute(location.pathname);
  }, [location.pathname]);

  // ═══════════════════════════════════════════════════════════
  // TUTORIAL MANUAL TRIGGER HANDLER
  // ═══════════════════════════════════════════════════════════
  const handleShowTutorial = () => {
    const tutorial = getTutorialForRoute(location.pathname);
    if (tutorial) {
      resetTutorial(tutorial.id);
      startTutorial(tutorial);
    } else {
      console.log('No tutorial available for this page');
    }
  };

  /* ——— FETCH CREATOR STATUS FROM API ——— */
  const cachedStatus = getStatusCache();
  
  const {
    data: statusData,
    isLoading: statusLoading,
    error: statusError,
    refetch: refetchStatus
  } = useQuery(
    ['creatorStatus'],
    () => API.creatorConnect.getStatus(),
    {
      enabled: !!creatorToken && isInitialized,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 60,
      initialData: cachedStatus ? { success: true, ...cachedStatus } : undefined,
      onSuccess: (data) => {
        if (data && data.success) {
          setStatusCache(data);
          console.log('✅ [CreatorNavbar] Status loaded:', data.status);
        }
      },
      onError: (error) => {
        console.error('❌ [CreatorNavbar] Failed to load status:', error);
      }
    }
  );

  const creatorStatus = statusData?.status || "Associate";
  const isPartner = creatorStatus === "Partner";
  const hasGmailConnected = statusData?.has_gmail_connected || false;
  const processEmails = statusData?.process_emails || false;

  /* ——— RESILIENT SUPERVISOR LOGIC ——— */
  const creator = creatorToken?.creator_user?.creator;
  
  const contextSupId = creator?.superviser;
  const cachedSupId = getLastSupervisorId();
  const supUserId = contextSupId || cachedSupId;

  const cachedData = getSupervisorCache();
  const shouldUseCachedData = cachedData && cachedData.userId === supUserId;

  const {
    data: supervisorRaw,
    isLoading: loadingSup,
    error: supErr,
    refetch: refetchSupervisor
  } = useQuery(
    ['supervisorProfile', supUserId],
    () => API.users.fetchPublicDetails(supUserId),
    { 
      enabled: !!supUserId && isInitialized,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 60 * 24,
      initialData: shouldUseCachedData ? { user: cachedData.data } : undefined,
      onSuccess: (data) => {
        const normalized = data?.user ?? data;
        if (normalized && supUserId) {
          setSupervisorCache(supUserId, normalized);
          console.log('✅ [CreatorNavbar] Successfully loaded and cached supervisor');
        }
      },
      onError: (error) => {
        console.error('❌ [CreatorNavbar] Failed to load supervisor:', error);
      }
    }
  );

  const supervisor = useMemo(() => {
    if (!supervisorRaw) return null;
    return supervisorRaw.user ?? supervisorRaw;
  }, [supervisorRaw]);

  useEffect(() => {
    if (contextSupId && isInitialized && !loadingSup && !supervisor) {
      console.log('🔄 [CreatorNavbar] Context ID now available, refetching supervisor');
      refetchSupervisor();
    }
  }, [contextSupId, isInitialized, loadingSup, supervisor, refetchSupervisor]);

  /* ——— CHAT MODAL STATE ——— */
  const [chatOpen, setChatOpen] = useState(false);
  const openChat = () => supUserId && setChatOpen(true);

  /* ——— CHAT UNREAD COUNT ——— */
  const { data: conversations = [] } = useQuery(
    ["creatorConversations", creatorToken?.creator_user?.username],
    API.conversations.fetchCreatorConversations,
    {
      enabled: !!creatorToken,
      staleTime: 1000 * 30,
      refetchOnWindowFocus: false,
    }
  );
  const unreadChatCount = useMemo(
    () => conversations.reduce((acc, c) => acc + (Number(c.unread_count) || 0), 0),
    [conversations]
  );

  /* ——— NOTIFICATION COUNTS - Using V2 API ——— */
  const {
    data: unreadNotifCountData,
    isLoading: notifCountLoading,
  } = useQuery(
    ["notifications", "v2", "unread_count"],
    () => API.notifications.getUnreadCount(),
    {
      enabled: !!creatorToken,
      staleTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    }
  );
  const unreadNotifCount = unreadNotifCountData?.unread_count || 0;

  const { data: notifCounts } = useQuery(
    ["notifications", "v2", "counts"],
    () => API.notifications.getCounts(),
    {
      enabled: !!creatorToken,
      staleTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    }
  );
  const unreadCollabCount = notifCounts?.unread_collab_count || 0;

  /* ——— NOTIFICATION HANDLERS ——— */
  const handleOpenNotifications = (e) => {
    setNotifAnchor(e.currentTarget);
  };

  const handleCloseNotifications = () => {
    setNotifAnchor(null);
  };

  /* ——— FETCH PENDING CONTRACTS ——— */
  const { data: pendingContracts } = useQuery(
    ["creatorPendingContracts", creatorToken?.creator_user?.username],
    () => API.contracts.getMyPendingContracts(),
    {
      enabled: !!creatorToken,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 30,
    }
  );

  const [activeContracts, setActiveContracts] = useState(null);
  const [contractPopupDismissed, setContractPopupDismissed] = useState(false);

  useEffect(() => {
    if (!pendingContracts?.pending_contracts || contractPopupDismissed) return;
    
    if (location.pathname !== routes.creatorConnectStart) {
      setActiveContracts(null);
      return;
    }
    
    const contracts = pendingContracts.pending_contracts;
    if (contracts.length > 0) {
      setActiveContracts(contracts);
    } else {
      setActiveContracts(null);
    }
  }, [pendingContracts, contractPopupDismissed, location.pathname]);

  const handleCloseContractPopup = () => {
    setActiveContracts(null);
    setContractPopupDismissed(true);
    setTimeout(() => setContractPopupDismissed(false), 1000 * 60 * 60);
  };

  /* ——— PROPAGATE UNREAD TO HEADER BADGE ——— */
  const totalMobileBadge = unreadChatCount + unreadNotifCount;
  useEffect(() => {
    onUnreadCountChange?.(totalMobileBadge);
  }, [totalMobileBadge, onUnreadCountChange]);

  /* ——— Mobile actions for parent ——— */
  const mobileActions = useMemo(() => [
    {
      key: 'notifications',
      icon: (
        <Badge badgeContent={notifCountLoading ? 0 : unreadNotifCount} color="secondary">
          <NotificationsNoneIcon />
        </Badge>
      ),
      onClick: handleOpenNotifications,
      tooltip: 'Notifications',
    },
    {
      key: 'trophy',
      icon: <TrophyIcon color="warning" />,
      onClick: () => navigate(routes.trophyRoom),
      tooltip: 'Trophy Room',
    },
    {
      key: 'settings',
      icon: <SettingsOutlinedIcon />,
      onClick: () => navigate(routes.creatorConnectEdit),
      tooltip: 'Account settings',
    },
  ], [unreadNotifCount, notifCountLoading, navigate]);

  useEffect(() => {
    if (!isDesktop && onGetMobileActions) {
      onGetMobileActions(mobileActions);
    }
  }, [isDesktop, mobileActions, onGetMobileActions]);

  /* ——— Auto-collapse bottom section on smaller screens ——— */
  useEffect(() => {
    if (isLargeScreen && isVeryTall) {
      setBottomSectionExpanded(false);
    } else {
      setBottomSectionExpanded(false);
    }
  }, [isLargeScreen, isVeryTall]);

  /* ——— PARTNER / AGENCY LOGIC ——— */
  const agency = creatorToken?.creator_user?.agency;
  const isAgencyRestricted = (a) => a && a !== "TCC";

  /* ——— NAV LINKS ———
     Money and Chat are now fully owned by the Next.js app (/money, /messages
     — both allow the Contractor account type, confirmed via blitz-frontend's
     route-access config) — removed so legacy sessions don't organically
     rediscover pages that have moved. Deals/Files/Partner Suite/Apps stay
     (not yet confirmed live on Next.js). See docs/legacy-fallback-pages.md. */
  const links = [
    {
      text: "Deals",
      route: routes.creatorCollabs,
      icon: (
        <Badge badgeContent={unreadCollabCount} color="secondary" max={999} invisible={!unreadCollabCount}>
          <HandshakeOutlined />
        </Badge>
      ),
    },
    {
      text: "Files",
      route: routes.files,
      icon: <InsertDriveFileOutlined />,
      tooltip: "Access and manage your files"
    },
    { text: "Partner Suite", route: routes.partnerSuite, icon: <StarIcon /> },
    { text: "Apps", route: routes.creatorConnectApps, icon: <AppsIcon /> },
  ];
  const filteredLinks = links.filter(
    (item) => !((item.text === "Deals" || item.text === "Sales") && isAgencyRestricted(agency))
  );

  /* ——— BASIC PROFILE INFO ——— */
  const displayName = creator?.display_name || creatorToken?.creator_user?.username;
  const photo = creator?.pfphref;

  /* ——— SUPERVISOR DISPLAY INFO ——— */
  const supName = supervisor
    ? `${supervisor.first_name ?? ''} ${supervisor.last_name ?? ''}`.trim() ||
      supervisor.username
    : '—';
  const supAvatar = supervisor?.phphref || '';

  /* ——— HANDLE STATUS REFRESH ——— */
  const handleRefreshStatus = async () => {
    console.log('🔄 [CreatorNavbar] Manually refreshing status');
    await refetchStatus();
  };

  /* ——— RENDER ——— */
  return (
    <>
      <IconDrawer
        anchor={anchor}
        variant={isDesktop ? "permanent" : "temporary"}
        open={isDesktop || isOpen}
        extended={isExtended}
        onClose={onClose}
        sx={{
          zIndex: (theme) => (isDesktop ? theme.zIndex.drawer : theme.zIndex.modal),
          color: "#000",
          "& .MuiDrawer-paper": {
            color: "#000",
            backgroundColor: "#FFF",
            top: 0,
            height: '100vh',
            overflowY: "auto",
            overflowX: "hidden",
            boxSizing: "border-box",
            display: 'flex',
            flexDirection: 'column',
            ...buildSafeAreaInset(anchor, isDesktop),
          },
        }}
      >
        {/* ENHANCED HEADER / LOGO SECTION */}
        <Box
          sx={{
            p: 2,
            display: { xs: "none", md: "flex" },
            flexDirection: "row",
            alignItems: "center",
            justifyContent: isExtended ? "space-between" : "center",
            flexShrink: 0,
            borderBottom: '1px solid rgba(0,0,0,0.12)',
            minHeight: 72,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isExtended && (
              <RouterLink 
                to={routes.creatorConnectStart}
                style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
              >
                <img 
                  src={logo} 
                  alt="Blitz Creator" 
                  style={{ height: 32, marginRight: 8 }}
                />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                    fontSize: '1.1rem'
                  }}
                >
                  Creator Hub
                </Typography>
              </RouterLink>
            )}
          </Box>

          <WhiteIconButton onClick={() => onToggleExtend?.()}>
            {isExtended ? (
              <KeyboardArrowLeftSharpIcon />
            ) : (
              <KeyboardArrowRightSharpIcon />
            )}
          </WhiteIconButton>
        </Box>

        {/* Enhanced Profile Section */}
        <Box sx={{ 
          p: isExtended ? 2 : 1.5, 
          display: "flex", 
          flexDirection: isExtended ? (isDesktop ? "row" : "column") : "column",
          alignItems: isExtended && isDesktop ? "flex-start" : "center",
          gap: isExtended ? 1.5 : 1,
          minHeight: isExtended ? 'auto' : 120,
          justifyContent: isExtended ? 'flex-start' : 'center',
          flexShrink: 0,
        }}>
          <Box sx={{ position: 'relative' }}>
            <Tooltip title={`Go to ${displayName}'s bio`} placement="right" arrow>
              <Avatar
                src={photo}
                alt={displayName}
                sx={{ 
                  width: isExtended ? 56 : 48, 
                  height: isExtended ? 56 : 48,
                  cursor: "pointer",
                  flexShrink: 0,
                  border: "2px solid",
                  borderColor: isPartner ? theme.palette.success.main : theme.palette.primary.main,
                  boxShadow: theme.shadows[2],
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  }
                }}
                onClick={() => navigate('/bio')}
              />
            </Tooltip>
            {isPartner && !isExtended && (
              <Tooltip title="Partner Status" placement="right" arrow>
                <Box sx={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid white',
                  boxShadow: theme.shadows[2]
                }}>
                  <VerifiedIcon sx={{ fontSize: 12, color: 'white' }} />
                </Box>
              </Tooltip>
            )}
          </Box>
          
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: isExtended && isDesktop ? 'flex-start' : 'center',
            flex: isExtended ? 1 : 'none',
            width: '100%',
            gap: isExtended ? 1 : 0.75
          }}>
            {isExtended && (
              <Box sx={{ 
                textAlign: isDesktop ? 'left' : 'center',
                width: '100%'
              }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    lineHeight: 1.2,
                    mb: 0.25
                  }}
                >
                  Hey, {displayName}!
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: isDesktop ? 'flex-start' : 'center' }}>
                  {statusLoading ? (
                    <CircularProgress size={12} sx={{ color: 'text.secondary' }} />
                  ) : statusError ? (
                    <Typography variant="caption" sx={{ color: 'error.main', fontSize: '0.7rem' }}>
                      Status unavailable
                    </Typography>
                  ) : (
                    <>
                      {isPartner ? (
                        <PartnerChip
                          icon={<VerifiedIcon />}
                          label="Partner"
                          size="small"
                        />
                      ) : (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                            lineHeight: 1.2,
                          }}
                        >
                          {creatorStatus}
                        </Typography>
                      )}
                      {isPartner && hasGmailConnected && (
                        <Tooltip title={processEmails ? "AI agent active" : "AI agent paused"} arrow>
                          <Chip
                            label={processEmails ? "🤖 Active" : "⏸️ Paused"}
                            size="small"
                            sx={{ 
                              height: 16,
                              fontSize: '0.65rem',
                              '& .MuiChip-label': { px: 0.5 }
                            }}
                            color={processEmails ? "success" : "default"}
                          />
                        </Tooltip>
                      )}
                      <Tooltip title="Refresh status" arrow>
                        <IconButton
                          size="small"
                          onClick={handleRefreshStatus}
                          disabled={statusLoading}
                          sx={{ 
                            p: 0.25,
                            ml: 0.5,
                            '&:hover': { backgroundColor: 'action.hover' }
                          }}
                        >
                          <RefreshIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
            )}
            
            <Box sx={{ 
              display: 'flex',
              flexDirection: isExtended ? 'row' : 'column',
              alignItems: 'center',
              justifyContent: isExtended && isDesktop ? 'space-between' : 'center',
              gap: isExtended ? 0.5 : 0.75,
              width: isExtended ? '100%' : 'auto'
            }}>
              <Box sx={{ 
                display: 'flex',
                flexDirection: isExtended ? 'row' : 'column',
                gap: isExtended ? 0.5 : 0.75,
                alignItems: 'center'
              }}>
                <Tooltip 
                  title="Notifications" 
                  placement={isExtended ? "bottom" : "right"}
                  arrow
                >
                  <IconButton 
                    onClick={handleOpenNotifications}
                    size={isExtended ? "medium" : "small"}
                    sx={{ 
                      color: 'text.primary',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    <Badge 
                      badgeContent={notifCountLoading ? 0 : unreadNotifCount} 
                      color="secondary"
                      max={99}
                    >
                      <NotificationsNoneIcon 
                        fontSize={isExtended ? "medium" : "small"} 
                      />
                    </Badge>
                  </IconButton>
                </Tooltip>
                
                <Tooltip 
                  title="Trophy Room" 
                  placement={isExtended ? "bottom" : "right"}
                  arrow
                >
                  <IconButton 
                    onClick={() => navigate(routes.trophyRoom)}
                    size={isExtended ? "medium" : "small"}
                    sx={{ 
                      color: 'text.primary',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    <TrophyIcon 
                      fontSize={isExtended ? "medium" : "small"}
                      color="warning"
                    />
                  </IconButton>
                </Tooltip>
                
                <Tooltip 
                  title="Account Settings" 
                  placement={isExtended ? "bottom" : "right"}
                  arrow
                >
                  <IconButton 
                    onClick={() => navigate(routes.creatorConnectEdit)}
                    size={isExtended ? "medium" : "small"}
                    sx={{ 
                      color: 'text.primary',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    <SettingsOutlinedIcon 
                      fontSize={isExtended ? "medium" : "small"} 
                    />
                  </IconButton>
                </Tooltip>
              </Box>

              {isExtended && isDesktop && (
                <Tooltip title="Log Out" placement="bottom" arrow>
                  <IconButton
                    onClick={() => {
                      clearAllCaches();
                      logout();
                      return window.location.href = '/';
                    }}
                    size="medium"
                    sx={{ 
                      color: 'text.primary',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'error.main',
                        color: 'white',
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    <LogoutIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
        
        <Divider sx={{ mx: 1, flexShrink: 0 }} />

        {/* Persistent escape hatch back to the Next.js app — every other
            feature has moved there. Placed near the top so it's immediately
            visible, not buried at the bottom. Goes to /money (the app
            itself), not the bare landing page — anyone using this is
            already authenticated. See docs/legacy-fallback-pages.md. */}
        <Tooltip title="Go to the Blitz app" placement="right">
          <Box
            onClick={() => {
              window.location.href = ConfigValue.NEXT_APP_URL
                ? `${ConfigValue.NEXT_APP_URL}/money`
                : routes.base;
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mx: 1,
              mb: 1,
              px: 1,
              py: 1,
              borderRadius: 2,
              cursor: "pointer",
              color: "text.secondary",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <OpenInNewOutlined fontSize="small" />
            {isExtended && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Back to Blitz
              </Typography>
            )}
          </Box>
        </Tooltip>

        {/* Main Navigation */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <Box 
            sx={{ 
              overflowY: isExtended ? "auto" : "clip", 
              flexGrow: 1,
              scrollbarColor: `rgba(0,0,0,0.24) transparent`,
              scrollbarWidth: "thin",
            }}
          >
            <List sx={{ pt: 1 }}>
              {filteredLinks.map((item) => (
                <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
                  <ListItemLink to={item.route} primary={item.text} icon={item.icon} isOpen={isExtended} />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* ENHANCED BOTTOM SECTION - WITH TUTORIAL TRIGGER */}
          <Box sx={{ flexShrink: 0 }}>
            <Divider sx={{ mx: 1 }} />

            {isExtended && (
              <Box sx={{ p: 1.5 }}>
                <BottomSectionCard>
                  {loadingSup ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : supErr ? (
                    <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                      <Typography variant="caption">
                        Unable to load supervisor info
                      </Typography>
                    </Alert>
                  ) : supervisor ? (
                    <>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          src={supAvatar}
                          alt={supName}
                          sx={{ 
                            width: 40, 
                            height: 40,
                            border: `2px solid ${theme.palette.primary.main}`,
                            boxShadow: theme.shadows[2]
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: 600,
                            display: 'block',
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {supName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                            Your Blitz Bestie ✨
                          </Typography>
                        </Box>
                        
                        <IconButton
                          size="small"
                          onClick={() => setBottomSectionExpanded(!bottomSectionExpanded)}
                          sx={{ 
                            p: 0.5,
                            backgroundColor: theme.palette.action.hover,
                            '&:hover': {
                              backgroundColor: theme.palette.action.selected,
                            }
                          }}
                        >
                          {bottomSectionExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      </Box>
                      
                      <Collapse in={bottomSectionExpanded}>
                        <Box sx={{ mt: 1.5 }}>
                          {(supervisor.email || supervisor.phone_number) && (
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              gap: 0.5,
                              mb: 1.5
                            }}>
                              {supervisor.email && (
                                <Chip
                                  icon={<EmailIcon fontSize="small" />}
                                  label={supervisor.email}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                              {supervisor.phone_number && (
                                <Chip
                                  icon={<PhoneIcon fontSize="small" />}
                                  label={supervisor.phone_number}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          )}
                          
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            flexWrap: 'wrap'
                          }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/profile/${supUserId}`)}
                              sx={{ 
                                textTransform: "none",
                                fontSize: '0.75rem',
                                flex: '1 1 auto',
                                minWidth: 'fit-content'
                              }}
                            >
                              Profile
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={openChat}
                              startIcon={<ChatBubbleOutlineIcon fontSize="small" />}
                              sx={{ 
                                textTransform: "none",
                                fontSize: '0.75rem',
                                flex: '1 1 auto',
                                minWidth: 'fit-content'
                              }}
                            >
                              Chat
                            </Button>

                            {/* Help Button */}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<LiveHelpOutlinedIcon fontSize="small" />}
                              onClick={() => navigate("/help")}
                              sx={{
                                textTransform: "none",
                                fontSize: '0.75rem',
                                flex: '1 1 100%',
                                minWidth: 'fit-content',
                                borderColor: theme.palette.info.main,
                                color: theme.palette.info.main,
                                '&:hover': {
                                  borderColor: theme.palette.info.dark,
                                  backgroundColor: theme.palette.info.light + "20",
                                },
                              }}
                            >
                              Need Help?
                            </Button>

                            {/* ═══════════════════════════════════════════════
                                TUTORIAL TRIGGER BUTTON
                                ═══════════════════════════════════════════════ */}
                            {currentTutorial && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<SchoolIcon fontSize="small" />}
                                onClick={handleShowTutorial}
                                sx={{
                                  textTransform: "none",
                                  fontSize: '0.75rem',
                                  flex: '1 1 100%',
                                  minWidth: 'fit-content',
                                  borderColor: theme.palette.secondary.main,
                                  color: theme.palette.secondary.main,
                                  '&:hover': {
                                    borderColor: theme.palette.secondary.dark,
                                    backgroundColor: theme.palette.secondary.light + "20",
                                  },
                                }}
                              >
                                Show Tutorial
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Collapse>
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.info.light,
                        color: theme.palette.info.main
                      }}>
                        <LiveHelpOutlinedIcon />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Need Help?
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Get support and answers
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="info"
                          onClick={() => navigate("/help")}
                          sx={{ 
                            textTransform: "none",
                            fontSize: '0.75rem'
                          }}
                        >
                          Help
                        </Button>
                        {currentTutorial && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<SchoolIcon fontSize="small" />}
                            onClick={handleShowTutorial}
                            sx={{
                              textTransform: "none",
                              fontSize: '0.65rem',
                            }}
                          >
                            Tutorial
                          </Button>
                        )}
                      </Box>
                    </Box>
                  )}
                </BottomSectionCard>
              </Box>
            )}
            
            {!isExtended && (
              <List sx={{ pb: 1 }}>
                <ListItem disablePadding>
                  <ListItemButton 
                    onClick={() => navigate("/help")}
                    sx={{
                      minHeight: 48,
                      justifyContent: 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: 'auto',
                      justifyContent: 'center'
                    }}>
                      <Tooltip title="Help" placement="right" arrow>
                        <LiveHelpOutlinedIcon />
                      </Tooltip>
                    </ListItemIcon>
                  </ListItemButton>
                </ListItem>
                
                {!isDesktop && (
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        clearAllCaches();
                        logout();
                        return window.location.href = '/';
                      }}
                      sx={{
                        minHeight: 48,
                        justifyContent: 'center',
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: 'auto',
                        justifyContent: 'center'
                      }}>
                        <Tooltip title="Log out" placement="right" arrow>
                          <LogoutIcon />
                        </Tooltip>
                      </ListItemIcon>
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            )}
          </Box>
        </Box>
      </IconDrawer>

      <UnifiedNotificationCenter
        anchorEl={notifAnchor}
        onClose={handleCloseNotifications}
        isCreator={true}
      />

      {activeContracts && (
        <ContractPopup
          open={Boolean(activeContracts)}
          contracts={activeContracts}
          onClose={handleCloseContractPopup}
          hideReviewAll={true}
        />
      )}

      <ChatModalCreator
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        recipientUserId={supUserId}
      />
    </>
  );
}